import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { OrderService } from 'src/app/core/services/order.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { FormControl } from '@angular/forms';

export interface SubstituteProduct {
  _id: string;
  productName: string;
  image: string;
  price: number;
  sku: string;
  category?: { name: string };
  brand?: { name: string };
  variants?: ProductVariant[];
  selectedVariant?: ProductVariant;
  quantity: number;
  isSelected: boolean;
}

export interface ProductVariant {
  _id: string;
  variantName: string;
  sellingPrice: number;
  sku: string;
  image?: string[];
  attributes?: { key: string; value: string }[];
}

export interface SubstituteModalData {
  orderId: string;
  orderNo: string;
  originalProduct?: {
    productName: string;
    image: string;
    quantity: number;
  };
}

@Component({
  selector: 'megastore-admin-substitute-products',
  templateUrl: './substitute-products.component.html',
  styleUrls: ['./substitute-products.component.scss']
})
export class SubstituteProductsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  products: SubstituteProduct[] = [];
  filteredProducts: SubstituteProduct[] = [];
  selectedProducts: SubstituteProduct[] = [];
  
  searchControl = new FormControl('');
  isLoading = false;
  
  // Pagination
  currentPage = 1;
  pageSize = 12;
  totalRecords = 0;
  totalPages = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SubstituteModalData,
    private dialogRef: MatDialogRef<SubstituteProductsComponent>,
    private orderService: OrderService,
    private spinnerService: SpinnerService
  ) {}

  ngOnInit(): void {
    this.loadProducts();
    this.setupSearch();
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.loadProducts();
      });
  }

  loadProducts(): void {
    this.isLoading = true;
    const searchTerm = this.searchControl.value || '';
    
    this.orderService
      .getSubstituteProducts(this.data.orderId, this.currentPage, this.pageSize, searchTerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.products = response.data.substituteProduct.map((product: any) => ({
            ...product,
            quantity: 1,
            isSelected: this.selectedProducts.some(p => p._id === product._id),
            selectedVariant: product.variants?.[0] || null
          }));
          this.filteredProducts = [...this.products];
          this.totalRecords = response.data.totleCount || 0;
          this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  toggleProductSelection(product: SubstituteProduct): void {
    if (product.isSelected) {
      // Remove from selected
      this.selectedProducts = this.selectedProducts.filter(p => p._id !== product._id);
      product.isSelected = false;
    } else {
      // Add to selected
      product.isSelected = true;
      this.selectedProducts.push({ ...product });
    }
  }

  updateSelectedProductQuantity(product: SubstituteProduct, change: number): void {
    const newQuantity = product.quantity + change;
    if (newQuantity >= 1 && newQuantity <= 99) {
      product.quantity = newQuantity;
      // Update in selected products array
      const selectedIndex = this.selectedProducts.findIndex(p => p._id === product._id);
      if (selectedIndex !== -1) {
        this.selectedProducts[selectedIndex].quantity = newQuantity;
      }
    }
  }

  selectVariant(product: SubstituteProduct, variant: ProductVariant): void {
    product.selectedVariant = variant;
    // Update in selected products array
    const selectedIndex = this.selectedProducts.findIndex(p => p._id === product._id);
    if (selectedIndex !== -1) {
      this.selectedProducts[selectedIndex].selectedVariant = variant;
    }
  }

  selectVariantNative(event: Event, product: SubstituteProduct): void {
    const selectElement = event.target as HTMLSelectElement;
    const variantId = selectElement.value;
    const variant = product.variants?.find(v => v._id === variantId);
    if (variant) {
      this.selectVariant(product, variant);
    }
  }

  removeFromSelected(product: SubstituteProduct): void {
    this.selectedProducts = this.selectedProducts.filter(p => p._id !== product._id);
    // Update the product in the grid
    const gridProduct = this.filteredProducts.find(p => p._id === product._id);
    if (gridProduct) {
      gridProduct.isSelected = false;
    }
  }

  clearAllSelected(): void {
    this.selectedProducts.forEach(selected => {
      const gridProduct = this.filteredProducts.find(p => p._id === selected._id);
      if (gridProduct) {
        gridProduct.isSelected = false;
      }
    });
    this.selectedProducts = [];
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadProducts();
  }

  sendSubstituteNotification(): void {
    if (this.selectedProducts.length === 0) {
      return;
    }

    this.spinnerService.addToLoader('sendSubstitute');
    
    const substituteData = this.selectedProducts.map(product => ({
      productId: product._id,
      variantId: product.selectedVariant?._id || null,
      quantity: product.quantity
    }));
    console.log('substituteData',substituteData);
    this.orderService
      .sendSubstituteNotification(this.data.orderId, substituteData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.spinnerService.removeFromLoader('sendSubstitute');
          this.dialogRef.close({ success: true });
        },
        error: () => {
          this.spinnerService.removeFromLoader('sendSubstitute');
        }
      });
  }

  acceptOrder(): void {
    this.spinnerService.addToLoader('acceptOrder');
    
    this.orderService
      .updateSubstituteProduct(this.data.orderId, {
        status: 2 // Accepted status
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.spinnerService.removeFromLoader('acceptOrder');
          this.dialogRef.close({ success: true, action: 'accepted' });
        },
        error: () => {
          this.spinnerService.removeFromLoader('acceptOrder');
        }
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  getProductImage(product: SubstituteProduct): string {
    if (product.selectedVariant?.image?.[0]) {
      return product.selectedVariant.image[0];
    }
    return product.image || 'assets/images/product-placeholder.png';
  }

  getProductPrice(product: SubstituteProduct): number {
    if (product.selectedVariant?.sellingPrice) {
      return product.selectedVariant.sellingPrice;
    }
    return 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

